import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { NAV_ITEMS, type NavItem } from '../../app/navigation';
import { useModals } from '../../app/Modals';
import { LocaleLink } from '../navigation/LocaleLink';

/**
 * Mobile navigation drawer — spec §25.2 ("mobile = hamburger drawer"), §654 (360px → 1440px+).
 *
 * The desktop nav is `hidden lg:flex`, so below 1024px there was previously NO site navigation
 * at all: every dropdown child (Hotels & Resorts, Brochures, …) was unreachable except through
 * the footer. This is that missing nav, not a convenience.
 *
 * There is no mobile design screen — the set in `Designe Screens/` is desktop-only — so this
 * follows the established system (panel surface, hairline borders, pill CTA) rather than
 * inventing a new visual language.
 *
 * ── Why a native <dialog> ───────────────────────────────────────────────────────────────────
 * Same reasoning as components/ui/Modal.tsx: `showModal()` gives focus trapping, Esc-to-close,
 * background inertness and top-layer stacking from the platform. §17 requires exactly that
 * contract, and it is the part a hand-rolled div gets subtly wrong.
 *
 * Nested dropdowns are flattened into inline accordions: a hover flyout has no meaning on a
 * touch screen, and stacking a second overlay on top of a drawer is a maze on a 360px display.
 */
export function MobileNavDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const modals = useModals();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { pathname } = useLocation();

  // Which accordion groups are expanded, keyed by labelKey.
  const [expanded, setExpanded] = useState<string[]>([]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  // Esc fires `close` natively — forward it so the parent's state cannot drift out of sync.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleClose = () => onClose();
    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, [onClose]);

  /*
   * Close on navigation. Tapping a link inside the drawer changes the route but leaves the
   * dialog open over the page it just navigated to — the drawer has to follow the router, not
   * only the link handlers, so that a browser Back gesture closes it too.
   */
  useEffect(() => {
    if (open) onClose();
    // Only pathname belongs in the deps: re-running when `open` changes would close it the
    // instant it opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // `showModal()` makes the background inert but does not reliably stop it scrolling behind
  // the drawer on mobile Safari.
  useEffect(() => {
    if (!open) return;
    const previous = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = previous;
    };
  }, [open]);

  const toggleGroup = (key: string) =>
    setExpanded((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    );

  return (
    <dialog
      ref={dialogRef}
      aria-label={t('nav.menu')}
      /*
       * Pinned to the right edge, full height. `m-0 ml-auto` overrides the centring a dialog
       * gets by default; `max-h-none` overrides the UA's max-height so it can be full-bleed.
       */
      className="m-0 ml-auto h-full max-h-none w-[min(22rem,88vw)] border-l border-line bg-panel p-0 text-content backdrop:bg-black/50 backdrop:backdrop-blur-sm"
      onClick={(event) => {
        // The backdrop is the dialog element itself, outside the panel content.
        if (event.target === dialogRef.current) onClose();
      }}
    >
      <div className="flex h-full flex-col">
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-line px-5">
          <span className="text-sm font-semibold uppercase tracking-widest text-content-muted">
            {t('nav.menu')}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('a11y.closeMenu')}
            className="-mr-2 inline-flex h-11 w-11 items-center justify-center rounded-pill text-content transition-colors hover:bg-content/5"
          >
            <span aria-hidden="true" className="text-lg">
              ✕
            </span>
          </button>
        </div>

        <nav aria-label={t('nav.menu')} className="flex-1 overflow-y-auto px-2 py-3">
          <ul>
            {NAV_ITEMS.map((item) => (
              <li key={item.labelKey}>
                <DrawerItem
                  item={item}
                  expanded={expanded.includes(item.labelKey)}
                  onToggle={() => toggleGroup(item.labelKey)}
                  onNavigate={onClose}
                />
              </li>
            ))}
          </ul>
        </nav>

        {/*
          "Become A Member" lives here too because the header pill is `hidden sm:inline-flex` —
          on a phone the drawer is the only place it exists. Closing first, rather than stacking
          the membership dialog on top of this one.
        */}
        <div className="shrink-0 border-t border-line p-4">
          <button
            type="button"
            onClick={() => {
              onClose();
              modals.open('membership');
            }}
            className="w-full rounded-pill bg-ink px-4 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90 dark:bg-white dark:text-ink"
          >
            {t('nav.becomeAMember')}
          </button>
        </div>
      </div>
    </dialog>
  );
}

const ROW = 'flex min-h-11 items-center rounded-lg px-3 py-2.5 text-base transition-colors';

function DrawerItem({
  item,
  expanded,
  onToggle,
  onNavigate,
}: {
  item: NavItem;
  expanded: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const children = item.children ?? [];

  if (children.length === 0) {
    return (
      <LocaleLink
        to={item.href ?? '/'}
        end={item.href === '/'}
        onClick={onNavigate}
        className={({ isActive }) =>
          `${ROW} ${isActive ? 'font-medium text-accent' : 'text-content hover:bg-content/5'}`
        }
      >
        {t(item.labelKey)}
      </LocaleLink>
    );
  }

  return (
    <>
      <div className="flex items-center">
        {/*
          A parent that is ALSO a link (What To See, §5) keeps both affordances as two separate
          targets: tapping the label goes to the hub, tapping the chevron expands. Collapsing
          them into one control would make the hub page unreachable from the drawer.
        */}
        {item.href ? (
          <LocaleLink
            to={item.href}
            onClick={onNavigate}
            className={({ isActive }) =>
              `${ROW} flex-1 ${isActive ? 'font-medium text-accent' : 'text-content hover:bg-content/5'}`
            }
          >
            {t(item.labelKey)}
          </LocaleLink>
        ) : (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            className={`${ROW} flex-1 text-left text-content hover:bg-content/5`}
          >
            {t(item.labelKey)}
          </button>
        )}

        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          // Names the section it controls, so a screen reader does not hear five identical
          // "expand" buttons.
          aria-label={t(expanded ? 'nav.collapseSection' : 'nav.expandSection', {
            section: t(item.labelKey),
          })}
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-content-muted transition-colors hover:bg-content/5"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
          >
            <path d="m5 7.5 5 5 5-5" />
          </svg>
        </button>
      </div>

      {expanded && (
        <ul className="mb-1 ml-3 border-l border-line pl-2">
          {children.map((child) => (
            <li key={child.labelKey}>
              <LocaleLink
                to={child.href}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `${ROW} text-sm ${
                    isActive ? 'font-medium text-accent' : 'text-content-muted hover:bg-content/5 hover:text-content'
                  }`
                }
              >
                {t(child.labelKey)}
              </LocaleLink>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
